import React, { useCallback, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PopUp from "../../containers/PopUp";
import ProductLocationPopUp from "../productPage/ProductLocationPopUp";
import { setBaseProducts } from "../../../server/ProductsFilters";
import { useProductStatusToggle } from "../../../hooks/useProductStatusToggle";
import {
  productStatusLabel,
  resolveProductActiveStatus,
  withProductStatus,
} from "../../../utils/productStatus";
import ProductStatusBadge from "../../productStatus/ProductStatusBadge";
import { getKashrutFeature } from "../../productInfo/productInfoLineUtils";

const getProduct = (row) => row.product || row;

export const formatProductBarcode = (product) => {
  if (!product?.gtin) return "-";
  const variant =
    product.variant != null &&
    product.variant !== "" &&
    String(product.variant) !== "000"
      ? product.variant
      : null;
  return variant != null ? `${product.gtin}-${variant}` : product.gtin;
};

const getFamilyName = (row) => {
  const hierarchy = row?.categories?.[0];
  if (!hierarchy?.length) return null;
  return hierarchy.at(-1).isFamily ? hierarchy.at(-1).slug : null;
};

const getCategoryValue = (row, level) => {
  const hierarchy = row?.categories?.[0];
  if (!hierarchy?.length || hierarchy?.length < level + 1) return "-";
  return !hierarchy?.[level]?.isFamily ? hierarchy?.[level]?.name : "-";
};

export const EXSTRA_COLUMNS = [
  {
    key: "kosherSupervision[0].classification",
    label: "בשרי/חלבי",
    width: "0.9fr",
    getValue: (row) =>
      getProduct(row).kosherSupervision?.[0]?.classification || "-",
    getSortValue: (row) =>
      getProduct(row).kosherSupervision?.[0]?.classification || "",
  },
  {
    key: "weightJump",
    label: "קפיצת משקל",
    width: "0.8fr",
    getValue: (row) => getProduct(row).weightJump ?? "-",
    getSortValue: (row) => getProduct(row).weightJump ?? 0,
  },
  {
    key: "description",
    label: "תיאור",
    width: "1.2fr",
    getValue: (row) => getProduct(row).description || "-",
    getSortValue: (row) => getProduct(row).description || "",
  },
  {
    key: "kosherSupervision[0].supervisionAgency.name",
    label: "כשרות",
    width: "1fr",
    getValue: (row) =>
      getProduct(row).kosherSupervision?.[0]?.supervisionAgency?.name || "-",
    getSortValue: (row) =>
      getProduct(row).kosherSupervision?.[0]?.supervisionAgency?.name || "",
  },
  {
    key: "userCompany",
    label: "יצרן",
    width: "1fr",
    getValue: (row) => getProduct(row).userCompany || "-",
    getSortValue: (row) => getProduct(row).userCompany || "",
  },
  {
    key: "weightDetails.weighedProduct",
    label: "שקיל",
    width: "0.7fr",
    getValue: (row) =>
      getProduct(row).weightDetails?.weighedProduct ? "כן" : "לא",
    getSortValue: (row) =>
      getProduct(row).weightDetails?.weighedProduct ? 1 : 0,
  },
  {
    key: "kosherSupervision[0].kashrutFeature",
    label: "כשרויות נוספות",
    width: "1fr",
    getValue: (row) => getKashrutFeature(getProduct(row)) || "-",
    getSortValue: (row) => getKashrutFeature(getProduct(row)),
  },
  {
    key: "vatExempt",
    label: "מע״מ",
    width: "0.7fr",
    getValue: (row) => (getProduct(row).vatExempt ? "פטור" : "חייב"),
    getSortValue: (row) => (getProduct(row).vatExempt ? 1 : 0),
  },
  {
    key: "showOutOfStock",
    label: "העלמת תמונה",
    width: "0.8fr",
    getValue: (row) => (getProduct(row).showOutOfStock ? "מוצג" : "מוסתר"),
    getSortValue: (row) => (getProduct(row).showOutOfStock ? 1 : 0),
  },
  {
    key: "verifyAge",
    label: "אישור 18+",
    width: "0.8fr",
    getValue: (row) => {
      const p = getProduct(row);
      return p.verifyAge === true || p.verify_age === true ? "כן" : "לא";
    },
    getSortValue: (row) => {
      const p = getProduct(row);
      return p.verifyAge === true || p.verify_age === true ? 1 : 0;
    },
  },
];

export const ALL_COLUMNS = [
  {
    key: "gtin",
    label: "ברקוד",
    width: "1.3fr",
    base: true,
    getValue: (row) => formatProductBarcode(getProduct(row)),
    getSortValue: (row) => {
      const barcode = formatProductBarcode(getProduct(row));
      return barcode === "-" ? "" : barcode;
    },
  },
  {
    key: "name",
    label: "שם מוצר",
    width: "minmax(160px, 3fr)",
    base: true,
    className: "product-name-cell",
    getValue: (row) => {
      const p = getProduct(row);
      return (
        <>
          {p.name || "-"}{" "}
          {p.weightDetails &&
            `${p.weightDetails.netContent} ${p.weightDetails.unitOfMeasure}`}
        </>
      );
    },
    getSortValue: (row) => getProduct(row).name || "",
  },
  {
    key: "subBrand",
    label: "מותג",
    width: "1.2fr",
    base: true,
    getValue: (row) => getProduct(row).subBrand?.name || "-",
    getSortValue: (row) => getProduct(row).subBrand?.name || "",
  },
  {
    key: "brand",
    label: "ספק",
    width: "1.2fr",
    base: true,
    getValue: (row) => getProduct(row).brand?.name || "-",
    getSortValue: (row) => getProduct(row).brand?.name || "",
  },
  {
    key: "department",
    label: "מחלקה",
    width: "1.3fr",
    base: true,
    getValue: (row) => getCategoryValue(row, 0),
    getSortValue: (row) => getCategoryValue(row, 0),
  },
  {
    key: "category",
    label: "קבוצה",
    width: "1.3fr",
    base: true,
    getValue: (row) => getCategoryValue(row, 1),
    getSortValue: (row) => getCategoryValue(row, 1),
  },
  {
    key: "subCategory",
    label: "תת-קבוצה",
    width: "1.3fr",
    base: true,
    getValue: (row) => getCategoryValue(row, 2),
    getSortValue: (row) => getCategoryValue(row, 2),
  },
  {
    key: "price",
    label: "מחיר",
    width: "0.9fr",
    base: true,
    getValue: (row) => (row.pricing?.price ? `₪${row.pricing.price}` : "-"),
    getSortValue: (row) => row.pricing?.price ?? 0,
  },
  {
    key: "stock",
    label: "מלאי זמין",
    width: "1fr",
    base: true,
    isStockColumn: true,
    getSortValue: (row) => row.availability?.availableQuantity ?? -1,
  },
  {
    key: "status",
    label: "סטטוס",
    width: "0.9fr",
    base: true,
    isStatusColumn: true,
    getValue: (row) =>
      productStatusLabel(resolveProductActiveStatus(row) || row.productStatus),
    getSortValue: (row) =>
      resolveProductActiveStatus(row) || row.productStatus || "",
  },
  {
    key: "family",
    label: "משפחה",
    width: "0.7fr",
    base: true,
    getValue: (row) => (getFamilyName(row) ? "✔" : "✘"),
    getSortValue: (row) => getFamilyName(row) ?? "\uFFFF",
  },

  ...EXSTRA_COLUMNS,
];

const ProductsTable = ({
  products,
  onSelect,
  isRowVisible,
  highlightProductId = null,
}) => {
  const dispatch = useDispatch();
  const [showPopup, setShowPopup] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const extraColumns = useSelector(
    (state) => state.productFilters.extraColumns,
  );
  const baseProducts = useSelector(
    (state) => state.productFilters.baseProducts,
  );
  const baseProductsRef = useRef(baseProducts);
  baseProductsRef.current = baseProducts;

  const applyCatalogAvailability = useCallback(
    (entries) => {
      const quantities = new Map();
      for (const entry of entries || []) {
        if (entry?.productId == null) continue;
        const availableQuantity = Number(entry.availableQuantity);
        if (!Number.isFinite(availableQuantity)) continue;
        quantities.set(String(entry.productId), availableQuantity);
      }
      if (!quantities.size) return;

      const next = (baseProductsRef.current || []).map((row) => {
        const product = getProduct(row);
        const rowId = String(product.productId ?? product.id ?? "");
        if (!quantities.has(rowId)) return row;
        const availableQuantity = quantities.get(rowId);
        const availability = {
          ...(row.availability || product.availability || {}),
          availableQuantity,
          availableInventory: availableQuantity,
        };
        if (row.product) {
          return {
            ...row,
            availability,
            product: { ...row.product, availability },
          };
        }
        return { ...row, availability };
      });
      dispatch(setBaseProducts(next));
    },
    [dispatch],
  );

  const applyLocalStatus = useCallback(
    (productId, status) => {
      const id = String(productId);
      const next = (baseProductsRef.current || []).map((row) => {
        const product = getProduct(row);
        const rowId = String(product.productId ?? product.id ?? "");
        if (rowId !== id) return row;
        if (row.product) {
          return {
            ...row,
            productStatus: status,
            product: withProductStatus(row.product, status),
          };
        }
        return withProductStatus(row, status);
      });
      dispatch(setBaseProducts(next));
    },
    [dispatch],
  );

  const { setProductStatus, statusPendingByProductId } =
    useProductStatusToggle(applyLocalStatus);

  const visibleColumns = useMemo(
    () =>
      ALL_COLUMNS.filter((col) => col.base || extraColumns.includes(col.key)),
    [extraColumns],
  );

  const gridTemplateColumns = visibleColumns
    .map((col) => col.width || "1fr")
    .join(" ");

  const sortedProducts = useMemo(() => {
    if (!sortKey) return products;

    const column = visibleColumns.find((col) => col.key === sortKey);

    if (!column) return products;

    return [...products].sort((a, b) => {
      const aVal = column.getSortValue(a);
      const bVal = column.getSortValue(b);

      const cmp =
        typeof aVal === "number" && typeof bVal === "number"
          ? aVal - bVal
          : String(aVal).localeCompare(String(bVal), "he");

      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [products, sortKey, sortDir, visibleColumns]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const handleAvailabilityClick = (e, row) => {
    e.stopPropagation();
    setSelectedProduct(getProduct(row));
    setShowPopup(true);
  };

  return (
    <>
      {/* Mobile-only sort control (the grid sort headers are hidden on mobile) */}
      <div className="products-sort-mobile">
        <label className="products-sort-mobile__field">
          <span className="products-sort-mobile__label">מיין לפי</span>
          <select
            className="products-sort-mobile__select"
            value={sortKey ?? ""}
            onChange={(e) => setSortKey(e.target.value || null)}
          >
            {visibleColumns.map((col) => (
              <option key={col.key} value={col.key}>
                {col.label}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="products-sort-mobile__dir"
          onClick={() =>
            setSortDir((prev) => (prev === "asc" ? "desc" : "asc"))
          }
          aria-label={sortDir === "asc" ? "סדר עולה" : "סדר יורד"}
          title={sortDir === "asc" ? "סדר עולה" : "סדר יורד"}
        >
          {sortDir === "asc" ? "▲" : "▼"}
        </button>
      </div>
      <div className="products-table">
        <div className="table-header" style={{ gridTemplateColumns }}>
          {visibleColumns.map((col) => (
            <div
              key={col.key}
              className="header-cell"
              onClick={() => handleSort(col.key)}
            >
              {col.label}
              {sortKey === col.key && (
                <span className="sort-arrow">
                  {sortDir === "asc" ? "↑" : "↓"}
                </span>
              )}
            </div>
          ))}
        </div>

        {sortedProducts.map((row, index) =>
          !isRowVisible || isRowVisible(index) ? (
            <div
              key={getProduct(row).productId || index}
              data-product-id={getProduct(row).productId || undefined}
              className={[
                "product-row",
                highlightProductId &&
                String(getProduct(row).productId) === String(highlightProductId)
                  ? "product-row--highlight"
                  : "",
              ]
                .filter(Boolean)
                .join(" ")}
              style={{ gridTemplateColumns }}
              onClick={() => onSelect(getProduct(row))}
            >
              {visibleColumns.map((col) => {
                if (col.isStockColumn) {
                  const qty = row.availability?.availableQuantity;
                  const hasStock = qty > 0;

                  return (
                    <div
                      key={col.key}
                      data-label={col.label}
                      className={`table-cell availability-cell ${
                        hasStock ? "in-stock" : "out-of-stock"
                      }`}
                      onClick={
                        hasStock
                          ? (e) => handleAvailabilityClick(e, row)
                          : undefined
                      }
                    >
                      {hasStock ? (
                        <>
                          <span className="stock-dot" />
                          <span>{qty}</span>
                          <span className="stock-icon">📦</span>
                        </>
                      ) : (
                        <>
                          <span className="stock-dot out" />
                          <span>{qty ?? "-"}</span>
                        </>
                      )}
                    </div>
                  );
                }

                if (col.isStatusColumn) {
                  const status =
                    resolveProductActiveStatus(row) || row.productStatus;
                  const productId = String(
                    getProduct(row).productId ?? getProduct(row).id ?? "",
                  );
                  const pending = !!statusPendingByProductId[productId];

                  return (
                    <div
                      key={col.key}
                      data-label={col.label}
                      className="table-cell status-cell"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ProductStatusBadge
                        status={status}
                        pending={pending}
                        onStatusChange={
                          status
                            ? (nextStatus) =>
                                setProductStatus(row, undefined, nextStatus)
                            : undefined
                        }
                      />
                    </div>
                  );
                }

                return (
                  <div
                    key={col.key}
                    data-label={col.label}
                    className={`table-cell ${col.className || ""}`}
                  >
                    {col.getValue(row)}
                  </div>
                );
              })}
            </div>
          ) : null,
        )}
      </div>

      {showPopup && selectedProduct && (
        <PopUp
          close={() => setShowPopup(false)}
          wrapperClassName="popup-wrapper--product-location"
        >
          <ProductLocationPopUp
            product={selectedProduct}
            isModal
            onInventoryUpdated={(productId, availability) =>
              applyCatalogAvailability([
                {
                  productId,
                  availableQuantity: availability?.availableQuantity,
                },
              ])
            }
            onCatalogAvailabilityUpdated={applyCatalogAvailability}
          />
        </PopUp>
      )}
    </>
  );
};

export default ProductsTable;
